/**
 * PWA install offer — capture Chrome's `beforeinstallprompt` and drive an
 * in-app affordance from it.
 *
 * Why this module exists at all: modern Chrome on Android does NOT show an
 * install prompt by itself. It fires `beforeinstallprompt` and waits for the
 * page to spend it from a user gesture; a site that never listens gets no
 * automatic offer, and the only remaining path is the browser's own ⋮ menu,
 * which nobody finds. ETM was in exactly that state — installable the whole
 * time, but silent — so the app was installed only by people who already
 * knew where to look.
 *
 * Split like lib/recaptcha.js: module-scope glue that owns global browser
 * state, over a pure core (resolveInstallMode/isSnoozed/detectIos) that
 * takes everything it needs as arguments and is unit-tested directly in
 * install.test.mjs without a DOM.
 *
 * The listener is attached from main.jsx BEFORE render() rather than from a
 * component effect. That is load-bearing: on a warm service-worker cache
 * Chrome can fire `beforeinstallprompt` before the first effect runs, and a
 * missed event is gone for the whole page load.
 */
import { installState } from '../store.js';

// The saved BeforeInstallPromptEvent. Single-use: once prompt() has been
// called on it, calling it again rejects, so it is nulled on spend. Chrome
// re-fires the event on later page loads while the app stays installable
// and uninstalled, so "hold the latest, spend it once, wait for the next
// load" is the correct model — not "cache it forever".
let deferredPrompt = null;
let installedThisSession = false;

const SNOOZE_KEY = 'etm.installOfferSnoozedUntil';
export const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// In-memory fallback for the snooze when localStorage is unavailable
// (private mode, storage disabled). Losing it on reload is acceptable —
// the offer is a hint, not state anyone depends on.
let memorySnoozedUntil = 0;

/* -------------------------------------------------------------------- */
/* Pure core — no DOM, no globals; every input is an argument.           */
/* -------------------------------------------------------------------- */

/**
 * Which install affordance this device can actually offer.
 *
 *  'installed' — already running as an installed app (or installed just now)
 *  'prompt'    — Chromium: we hold a live beforeinstallprompt event
 *  'ios'       — Safari/iOS: no API at all, instructions are the only path
 *  'none'      — nothing useful to offer (Firefox, or not yet installable)
 */
export function resolveInstallMode({ installed, hasPrompt, standalone, ios }) {
  if (installed || standalone) return 'installed';
  if (hasPrompt) return 'prompt';
  if (ios) return 'ios';
  return 'none';
}

/** Snooze is a plain timestamp comparison; the clock is passed in. */
export function isSnoozed(snoozedUntil, nowMs) {
  return Number(snoozedUntil) > nowMs;
}

/**
 * iOS/iPadOS detection. Deliberately user-agent based: there is no feature
 * test for "this browser installs only via the share sheet". The second
 * clause catches iPadOS 13+, which reports itself as Macintosh and is only
 * distinguishable by having a touch screen.
 */
export function detectIos(userAgent, maxTouchPoints) {
  const ua = String(userAgent || '');
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  return /Macintosh/.test(ua) && Number(maxTouchPoints) > 1;
}

/* -------------------------------------------------------------------- */
/* Glue — reads the real browser.                                        */
/* -------------------------------------------------------------------- */

function isStandalone() {
  try {
    if (typeof window === 'undefined') return false;
    // navigator.standalone is the iOS-only signal; the media queries cover
    // every Chromium/Android install and desktop window.
    if (window.navigator && window.navigator.standalone === true) return true;
    if (typeof window.matchMedia !== 'function') return false;
    return ['standalone', 'fullscreen', 'minimal-ui'].some(
      (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
    );
  } catch (e) {
    return false;
  }
}

function readSnoozedUntil() {
  try {
    const stored = window.localStorage.getItem(SNOOZE_KEY);
    if (stored) return Number(stored) || 0;
  } catch (e) {
    // Storage unavailable — fall through to the in-memory value.
  }
  return memorySnoozedUntil;
}

function writeSnoozedUntil(until) {
  memorySnoozedUntil = until;
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(until));
  } catch (e) {
    // Private mode / storage disabled: the in-memory value still holds for
    // this page load, which is the whole guarantee we need.
  }
}

/** Nudges the signal so InstallCard/SettingsScreen re-render. */
function bump() {
  installState.value = installState.value + 1;
}

/**
 * Attach the listeners. Called once from main.jsx before render().
 */
export function initInstallPrompt() {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress whatever ambient UI Chrome would have shown, so the offer
    // appears where we control it (and only once we are ready to explain it).
    if (typeof event.preventDefault === 'function') event.preventDefault();
    deferredPrompt = event;
    bump();
  });

  // Fires for ANY install, including one done through the browser's own ⋮
  // menu — the reliable "stop offering now" signal.
  window.addEventListener('appinstalled', () => {
    installedThisSession = true;
    deferredPrompt = null;
    bump();
  });
}

export function installMode() {
  return resolveInstallMode({
    installed: installedThisSession,
    hasPrompt: deferredPrompt !== null,
    standalone: isStandalone(),
    ios: typeof navigator === 'undefined'
      ? false
      : detectIos(navigator.userAgent, navigator.maxTouchPoints),
  });
}

/**
 * Whether the dashboard card should be on screen. The Settings row
 * deliberately ignores the snooze — it is the recovery path someone is
 * pointed at over the phone ("open Meer, tap App installeren").
 */
export function installOfferVisible(nowMs) {
  const mode = installMode();
  if (mode !== 'prompt' && mode !== 'ios') return false;
  return !isSnoozed(readSnoozedUntil(), nowMs);
}

export function snoozeInstallOffer(nowMs) {
  writeSnoozedUntil(nowMs + SNOOZE_MS);
  bump();
}

/**
 * Spend the saved event. Must be called directly from a click handler:
 * prompt() outside a user gesture rejects with NotAllowedError. Everything
 * before the first `await` here runs synchronously inside the caller's
 * gesture, which is what keeps that true.
 *
 * Resolves 'accepted' | 'dismissed' | 'unavailable'.
 */
export async function promptInstall() {
  const event = deferredPrompt;
  if (!event) return 'unavailable';
  deferredPrompt = null; // single-use: never prompt() the same event twice
  bump();
  try {
    event.prompt();
    const choice = await event.userChoice;
    return (choice && choice.outcome) || 'dismissed';
  } catch (e) {
    // NotAllowedError (no gesture) or InvalidStateError (already used).
    // The event is spent either way; the next page load re-fires it.
    return 'unavailable';
  }
}

/** Test seam: reset module state between scenarios. */
export function __resetInstallStateForTests() {
  deferredPrompt = null;
  installedThisSession = false;
  memorySnoozedUntil = 0;
}
